import { jwtDecode } from "jwt-decode";
import { ENDPOINTS, getFullUrl } from "./config";
import { ApiResponse, LoginRequest, LoginResponse } from "./types";
import { DecodedToken, User } from "../../types/auth";

export class AuthService {
  static async login(
    credentials: LoginRequest
  ): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.AUTH.LOGIN), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Login failed",
      };
    }
  }

  static async logout(): Promise<void> {
    localStorage.removeItem("token");
    localStorage.removeItem("decoded_token");
    localStorage.removeItem("auth_user");
  }

  static getToken(): string | null {
    return localStorage.getItem("token");
  }

  static setToken(token: string): void {
    localStorage.setItem("token", token);
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      localStorage.setItem("decoded_token", JSON.stringify(decoded));
    } catch (error) {
      console.error("Error storing decoded token:", error);
    }
  }

  static getDecodedToken(): DecodedToken | null {
    const stored = localStorage.getItem("decoded_token");
    return stored ? JSON.parse(stored) : null;
  }

  static isTokenValid(): boolean {
    const decoded = this.getDecodedToken();
    if (!decoded) return false;

    // Check if token is expired
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp > currentTime;
  }

  static async fetchUserDetails(): Promise<User | null> {
    try {
      const token = this.getToken();
      // console.log("Token:", token);
      if (!token) throw new Error("No token found");

      const url = getFullUrl(ENDPOINTS.AUTH.FETCHLOGINDETAILSFROMTOKEN);
    // console.log("API URL:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
         Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const data = await response.json();
      // console.log("API Response:", data);
      if (data.success) {
        return data.data; 
      } else {
        throw new Error(data.message || "Failed to fetch user details");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      return null;
    }
  }

  static async sendOTP(number: string) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.AUTH.SENDOTP), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify({
          mobileNumber: number,
        }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Login failed",
      };
    }
  }

  static async verifyOTP(number: string, otp: string) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.AUTH.VERIFYOTP), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify({
          mobileNumber: number,
          otp: otp,
        }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Login failed",
      };
    }
  }

  static async updatePassword(mobileNumber: string, newPassword: string) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.AUTH.UPDATEPASS), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify({
          mobileNumber,
          newPassword,
        }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Password update failed",
      };
    }
  }
  static async fetchNotificationTokens(userId:string):Promise<string[]>{
    try{
      const url =`${getFullUrl(ENDPOINTS.AUTH.FETCHUSERNOTIFICATIONTOKEN)}?id=${userId}`
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });
      const json = await response.json();

      if (!json.success) {
        return {
          success: false,
          message: json.message || "Failed to fetch notification tokens",
        };
      }

      // Deduplicate based on notificationToken
      const uniqueTokens = Array.from(
        new Set(json.data.map((item: { notificationToken: unknown; }) => item.notificationToken))
      );
      return uniqueTokens as string[];
    }catch(error){
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch notification tokens of doctor",
      };
    }
  }

  static async sendNotification(title: string, message: string, token: string[]) {
    try {
      const url = getFullUrl(ENDPOINTS.NOTIFICATION.SEND_NOTIFICATION);
      const body = {
        title,
        message,
        tokens:token,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      return data;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Notification sending failed",
      };
    }
  }

  static async createNotification(title: string, message: string, recieverId: string,recieverRole:string) {
    try {
      const url = getFullUrl(ENDPOINTS.NOTIFICATION.CREATENOTIFICATION);
      const body = {
        title,
        message,
        recieverRole,
        recieverId
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      return data;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Notification creation failed",
      };
    }
  }

}
